import { Children, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion, useScroll } from 'motion/react';

import { CaretDownIcon, CaretUpIcon, colors, useResizeObserver } from '@ui';

import * as css from './ParameterTable.css';

type ParameterTableProps = {
    referenceHeight?: number;
    scrollableThresholdPx?: number;
};

type ScrollDirection = 'up' | 'down' | undefined;

// Parameter table that knows whether it's scrollable or not.
// Displays a 'go down' | 'go up' button to allow the user to see parameters that are out of view.
export function ParameterTable({
    referenceHeight,
    scrollableThresholdPx = 10,
    children,
}: PropsWithChildren<ParameterTableProps>) {
    const ref = useRef<HTMLDivElement>(null);
    const [scrollDirection, set] = useState<ScrollDirection>('down');

    const { height = 0, scrollHeight = 0 } = useResizeObserver({
        ref,
    });

    const isValid = typeof height === 'number' && height > 0 && typeof scrollHeight === 'number' && scrollHeight > 0;
    const isScrollable = isValid && height < scrollHeight && scrollHeight - height > scrollableThresholdPx;

    const scrollHandler = useCallback(
        (progress?: number) => {
            if (!isScrollable || typeof progress !== 'number') {
                set(undefined);
                return;
            }

            if (progress <= 0) {
                set('down');
            } else if (progress >= 1) {
                set('up');
            } else {
                set(undefined);
            }
        },
        [isScrollable],
    );

    useEffect(() => {
        if (!isScrollable || !ref.current) {
            set(undefined);
            return;
        }
        const el = ref.current;
        const maxScrollTop = el.scrollHeight - el.clientHeight;

        if (maxScrollTop <= scrollableThresholdPx) set(undefined);
        else if (el.scrollTop <= scrollableThresholdPx) set('down');
        else if (el.scrollTop >= maxScrollTop - scrollableThresholdPx) set('up');
        else set(undefined);
    }, [isScrollable, scrollHeight, height, scrollableThresholdPx]);

    const { scrollYProgress } = useScroll({ container: ref });

    const childrenCount = Children.count(children);

    useEffect(() => {
        const reference = ref.current;
        const unsubscribe = scrollYProgress.on('change', scrollHandler);

        return () => {
            reference?.scrollTo({ top: 0, behavior: 'instant' });
            unsubscribe();
        };
    }, [scrollHandler, scrollYProgress, childrenCount]);

    const onTriggerScroll = useCallback(
        (direction: 'up' | 'down') => {
            if (isScrollable) {
                const scrollDestination = direction === 'down' ? scrollHeight : 0;
                ref.current?.scrollTo({ top: scrollDestination, behavior: 'smooth' });
            }
        },
        [isScrollable, scrollHeight],
    );

    return (
        <motion.div className={css.tableLayout({ scrollDirection })} style={{ minHeight: referenceHeight }}>
            <div className={css.table} style={{ maxHeight: referenceHeight }} ref={ref}>
                {children}
            </div>
            {referenceHeight && (
                <>
                    <div className={css.triggerButtonLayout({ direction: 'up' })}>
                        <AnimatePresence mode="sync">
                            {scrollDirection === 'up' && (
                                <motion.button
                                    className={css.triggerButton}
                                    onClick={() => onTriggerScroll('up')}
                                    initial={{ y: -32, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -32, opacity: 0 }}
                                >
                                    <CaretUpIcon size={24} color={colors.bluegray[600]} />
                                </motion.button>
                            )}
                        </AnimatePresence>
                    </div>
                    <div className={css.triggerButtonLayout({ direction: 'down' })}>
                        <AnimatePresence mode="sync">
                            {scrollDirection === 'down' && (
                                <motion.button
                                    className={css.triggerButton}
                                    onClick={() => onTriggerScroll('down')}
                                    initial={{ y: 32, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: 32, opacity: 0 }}
                                >
                                    <CaretDownIcon size={24} color={colors.bluegray[600]} />
                                </motion.button>
                            )}
                        </AnimatePresence>
                    </div>
                </>
            )}
        </motion.div>
    );
}
